<template>
  <fieldset>
    <div class="tla-phone-number" v-if="stepNumber === currentStep">
      <div class="tla-phone-number__copy">{{phoneCopy}}</div>
      <div class="tla-form-group tla-form-group">
        <label class="tla-form-group__label tla-form-group__label--required" for="telephone" v-html="phoneLabel"></label>
        <div class="tla-form-group__wrapper">
          <input
            id="telephone"
            autocomplete="tel"
            type="tel"
            v-validate.initial="'required'"
            data-vv-name="telephone"
            :data-vv-scope="validationScope"
            class="tla-form-group__input"
            :class="{'tla-form-group__input--error': (phoneNumberAttempted && errors.first(`${validationScope}.enhanced-telephone`))}"
            v-model="telephone"
            name="telephone"
            spellcheck="false"
            :placeholder="phonePlaceholder">
          <span v-if="phoneLoading" class="tla-form-group__wrapper--loading"></span>
        </div>
        <span class="tla-form-group__message tla-form-group__message--error tla-form-group__message--spaced" v-if="(phoneNumberAttempted && errors.first(`${validationScope}.enhanced-telephone`))">{{ errors.first(`${validationScope}.enhanced-telephone`) }}</span>
      </div>
    </div>
  </fieldset>
</template>

<script lang="ts">
import VeeValidate, {
  Validator
} from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import {
  State,
  Getter,
  Action,
  Mutation,
  namespace
} from 'vuex-class';
import Debounce from 'debounce-decorator';
import { validationService } from '../../services/ValidationService';
import { validationApiService } from '../../services/ValidationApiService';
import PersonalDetailsValidationMessages from '../../validation/personal-details';

const PersonalDetailsModule = namespace('personalDetailsModule');

Vue.use(VeeValidate, PersonalDetailsValidationMessages);

@Component
export default class PhoneNumberComponent extends Vue {
  @PersonalDetailsModule.Action public submitTelephone;

  @PersonalDetailsModule.Action public submitIsPrePhoneEntry;

  @PersonalDetailsModule.Getter public personalDetails;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'We may need to contact you to confirm your test drive booking' })
  public phoneCopy!: string;

  @Prop({ default: 'Phone <span>*</span>' })
  public phoneLabel!: string;

  @Prop({ default: 'Provide number here...' })
  public phonePlaceholder!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public useEnhancedPhoneNumberValidation!: boolean;

  private phoneNumberAttempted: boolean = false;

  private phoneLoading: boolean = false;

  public stepNumber = 0;

  get telephone() {
    return this.personalDetails.telephone;
  }

  set telephone(telephone: string) {
    this.submitTelephone(telephone);
  }

  public get currentStep(): number {
    return this.$store.getters['enquiryModule/currentStep'];
  }

  public get isNextClicked() {
    return validationService.hasNextBeenClicked(this.validationScope);
  }

  @Watch('isNextClicked')
  public watchIsNext() {
    this.phoneNumberAttempted = true;
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  @Watch('telephone')
  public async watchPhoneNumber() {
    if (this.stepNumber === this.currentStep) {
      this.$validator.errors.add({
        field: 'enhanced-telephone',
        msg: 'Please enter a valid phone number',
        scope: this.validationScope,
        id: 'enhanced-telephone'
      });

      this.validatePhoneNumber();
    }
  }

  @Debounce(500)
  public async validatePhoneNumber() {
    this.phoneLoading = true;
    const result = await validationApiService.isPhoneNumberValid(this.telephone, this.useEnhancedPhoneNumberValidation);

    if (result) {
      this.phoneLoading = false;
      this.$validator.errors.removeById('enhanced-telephone');
      this.submitIsPrePhoneEntry(true);
    } else {
      this.phoneLoading = false;
      this.submitIsPrePhoneEntry(false);
    }

    this.phoneNumberAttempted = true;
    this.validateComponent();
  }

  public async validateComponent() {
    await this.$validator.validateAll(this.validationScope);
  }

  public created() {
    this.stepNumber = this.currentStep;
    this.$validator.localize('en', PersonalDetailsValidationMessages.english);
  }
}
</script>
